/**
 * Contracts and pure policy for the task schedule card: the editor-launcher seam, the injected clock,
 * and the text/chip derivations the card and its popovers render.
 *
 * The clock is a provider rather than `new Date()` so a next-occurrence list is reproducible in a test
 * and identical for every consumer of the card within one change detection pass. Every date crossing
 * this boundary is a local `YYYY-MM-DD` string — the same currency the shared engine speaks — because a
 * `Date` carries a zone that turns a picked day into the day before west of Greenwich.
 */

import { InjectionToken } from '@angular/core';
import { ScheduleRule } from '@jms/model/scheduling/schedule.rule';
import { ScheduleContext, ScheduleExceptions, ScheduleNonWorkingDayPolicy, ScheduleSkip } from '@jms/model/scheduling';
import { ScheduleEngine } from '@jms/model/scheduling/schedule.engine';
import { CalendarDate, todayCalendarDate } from '@workbuddy/ui/v2/components/calendar';

/**
 * Stateless and pure — one instance backs every card and popover.
 *
 * Every entry point the card uses takes `undefined` to mean the working-day default, so the card never
 * branches around an absent schedule before asking the engine about it.
 */
export const SCHEDULE_ENGINE = new ScheduleEngine();

/**
 * Copy for the three schedule states the card can be in.
 *
 * The default is not an empty state: a task with no schedule of its own is due every working day of the
 * plan, which is a real cadence with real dates. Its title therefore states the rule and its subtitle
 * names where the working days come from — the plan — rather than asking the user to fill something in.
 *
 * Per visit is the opposite: an explicit tack-on that creates no dates at all, so its subtitle spells
 * out that it only ever joins a visit another task caused.
 */
export const WORKING_DAY_DEFAULT_TITLE = 'Due every working day';

export const WORKING_DAY_DEFAULT_SUBTITLE = 'From the plan\'s working days — set a schedule to change when';

export const PER_VISIT_SUBTITLE = 'Tacked onto any visit on this line';

/** How many upcoming dates render as chips before the rest collapse into `+ N more this year`. */
export const NEXT_OCCURRENCE_CHIP_LIMIT = 3;


/**
 * The card title: the engine's summary, with the task's own start named when it has one.
 *
 * A dates schedule takes the SHORT form. `describe()` spells out every listed date with its year
 * ('13 Aug 2026 · 24 Sep 2026 · …'), which is the full list rather than a title; `describeShort()` is
 * already the title shape — '13 Aug · 24 Sep' for a short list, '5 dates' beyond that — and the list
 * itself is the Next row directly below.
 *
 * A stored `startsOn` is always a genuine override — the editor writes it only when the user picks a
 * date other than "With the Service Line" — so the clause is never noise repeating the line's own
 * start back at the reader. It is appended only on a calendar schedule: a dates list already names its
 * first day, per visit has no dates to start, and after-service chains off the last service.
 */
export function buildScheduleTitle(schedule: ScheduleRule | null | undefined, startsOn: CalendarDate | undefined): string {

  if (!schedule) return WORKING_DAY_DEFAULT_TITLE;

  if (schedule.kind === 'dates') return SCHEDULE_ENGINE.describeShort(schedule);

  const summary = SCHEDULE_ENGINE.describe(schedule);

  if (!startsOn || schedule.kind !== 'calendar') return summary;

  return `${summary} · from ${formatFromDate(startsOn)}`;
}

/** '13 Aug' — the from-clause drops the weekday and year the title has no room for. */
function formatFromDate(date: CalendarDate): string {

  return `${Number(date.slice(8, 10))} ${MONTH_LABELS[Number(date.slice(5, 7)) - 1]}`;
}

/** Reads the current local date. Injected so occurrence maths is pinnable in a test. */
export type ScheduleTodayProvider = () => CalendarDate;

export const SCHEDULE_TODAY = new InjectionToken<ScheduleTodayProvider>('SCHEDULE_TODAY', {
  providedIn: 'root',
  factory: () => () => todayCalendarDate(),
});

/** Working days and holidays the card evaluates against. Absent parts fall back to a Mon–Fri week. */
export interface ScheduleCardContext {
  readonly effectiveFrom?: CalendarDate;
  readonly effectiveTo?: CalendarDate;
  readonly timezone?: string;
  readonly workWeek?: ScheduleContext['workWeek'];
  readonly holidays?: readonly string[];
  readonly lastCompleted?: CalendarDate;
  readonly today?: CalendarDate;
  /**
   * The task's own start override, if it has one. Distinct from `effectiveFrom`, which is already the
   * later of the line's start and this value: the title needs to know whether an override EXISTS, and
   * a folded effectiveFrom cannot answer that.
   */
  readonly startsOn?: CalendarDate;
}

const DEFAULT_WORK_WEEK: ScheduleContext['workWeek'] = [1, 2, 3, 4, 5];

/**
 * Builds the engine context. `effectiveFrom` defaults to today rather than to an epoch: without a line
 * anchor the only honest start for a preview is now, and an epoch anchor would shift every weekly
 * pattern onto the wrong weekday.
 */
export function buildScheduleContext(today: CalendarDate, context: ScheduleCardContext | null | undefined): ScheduleContext {

  return {
    effectiveFrom: context?.effectiveFrom ?? today,
    effectiveTo: context?.effectiveTo,
    timezone: context?.timezone ?? 'UTC',
    workWeek: context?.workWeek?.length ? context.workWeek : DEFAULT_WORK_WEEK,
    holidays: context?.holidays ? [...context.holidays] : undefined,
    lastCompleted: context?.lastCompleted,
    today: context?.today ?? today,
  };
}

/** 31 December of the year `today` falls in — the horizon the `+ N more this year` count is taken over. */
export function endOfYear(today: CalendarDate): CalendarDate {

  return `${today.slice(0, 4)}-12-31`;
}

/**
 * Every occurrence from today through year end. The card slices chips off the front and counts the tail.
 *
 * `undefined` is passed straight through rather than short-circuited to `[]`: absent now means "due
 * every working day", which is a schedule with real dates, and the engine is the only thing that knows
 * which days the context's work week actually covers.
 */
export function upcomingOccurrences(schedule: ScheduleRule | null | undefined, today: CalendarDate, context: ScheduleCardContext | null | undefined, exceptions: ScheduleExceptions | undefined): CalendarDate[] {

  return SCHEDULE_ENGINE.occurrencesBetween(schedule ?? undefined, today, endOfYear(today), buildScheduleContext(today, context), exceptions);
}

/** A single skip counts as one date; a range counts every inclusive day it covers. */
export function countSkippedDates(skips: readonly ScheduleSkip[] | undefined): number {

  let total = 0;

  for (const skip of skips ?? []) {
    if (skip.date) {
      total += 1;
      continue;
    }

    if (skip.from && skip.to) total += inclusiveDayCount(skip.from, skip.to);
  }

  return total;
}

/** Inclusive day span between two `YYYY-MM-DD` dates. Parts-built `Date`s keep this DST- and zone-free. */
function inclusiveDayCount(from: CalendarDate, to: CalendarDate): number {

  const start = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const end = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));

  return Math.max(0, Math.round((end - start) / 86_400_000) + 1);
}

/** `2 skipped dates · 1 extra date`, or an empty string when the task has no exceptions at all. */
export function describeExceptions(exceptions: ScheduleExceptions | undefined): string {

  const skipped = countSkippedDates(exceptions?.skips);
  const extras = exceptions?.extras?.length ?? 0;
  const parts: string[] = [];

  if (skipped > 0) parts.push(`${skipped} skipped ${skipped === 1 ? 'date' : 'dates'}`);
  if (extras > 0) parts.push(`${extras} extra ${extras === 1 ? 'date' : 'dates'}`);

  return parts.join(' · ');
}


/** Trade language for the non-working-day policy. `keep` is the default and says nothing on the card. */
const NON_WORKING_DAY_PHRASES: Record<ScheduleNonWorkingDayPolicy, string> = {
  keep: '',
  skip: 'skips weekends and holidays',
  next: 'moves to the next working day',
  previous: 'moves to the previous working day',
  nearest: 'moves to the nearest working day',
};

/**
 * The card subtitle: what this schedule state means, then the exception digest and the weekend/holiday
 * rule, whichever of those are present.
 *
 * Absent and per visit each lead with a sentence rather than a digest, because both are states a reader
 * can misread as the other: absent creates dates on its own and per visit never does.
 */
export function buildScheduleSubtitle(schedule: ScheduleRule | null | undefined, exceptions: ScheduleExceptions | undefined): string {

  const parts: string[] = [];
  if (!schedule) parts.push(WORKING_DAY_DEFAULT_SUBTITLE);
  if (schedule?.kind === 'perVisit') parts.push(PER_VISIT_SUBTITLE);

  const digest = describeExceptions(exceptions);
  if (digest) parts.push(digest);

  const policy = schedule?.calendar?.nonWorkingDayPolicy;
  const phrase = policy ? NON_WORKING_DAY_PHRASES[policy] : '';
  if (phrase) parts.push(phrase);

  return parts.join(' · ');
}

const WEEKDAY_LABELS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_LABELS: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `Tue 1 Sep` — the chip label. Built from numeric parts so it never re-parses through a timezone. */
export function formatChipDate(date: CalendarDate): string {

  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));
  const weekday = (new Date(year, month, day).getDay() + 6) % 7;

  return `${WEEKDAY_LABELS[weekday]} ${day} ${MONTH_LABELS[month]}`;
}

/** `Fri 25 Dec 2026` — the long form used in date fields and impact notes. */
export function formatLongDate(date: CalendarDate): string {

  return `${formatChipDate(date)} ${date.slice(0, 4)}`;
}

/** `Fri 25 Dec and Tue 29 Dec`, or an Oxford-free comma list beyond two. */
export function joinDates(dates: readonly CalendarDate[]): string {

  const labels = dates.map(formatChipDate);
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
